// <copyright file="quote-row-capabilities.ts" company="SoftTech Ltd">
// Copyright (c) SoftTech Ltd. All rights reserved.
// </copyright>

import { QuoteSetSibling, QuoteState } from '../../../api/dealer-api-interface-quote.js';
import { QuoteAlternativeType, QuoteLockState, ViewQuoteSummary } from '../../../api/dealer-api-interface-all.js';
import { IQuoteStateContext } from '../../../quotes/data/quote-state-engine.js';
import { QuoteSetSiblingManager, UserQuoteStateEngine } from '../data/quote-state-engine.js';
import { emptyGuid } from '../../../../webmodule-common/other/api/guid.js';
import { flagInSet } from '../../../../webmodule-common/other/ui/string-helper-functions.js';

/**
 * Id used for the synthetic entry that puts the row's own quote back into its set for the one
 * set-level rule that needs it (see `computeQuoteRowCapabilities`). It never has to match the real
 * quote id: nothing compares ids on the whole-set list, and a sentinel makes it obvious in a debugger
 * that the entry is synthesised rather than loaded.
 */
const SELF_IN_SET_ID = 'self-in-set';

/**
 * Everything an ellipsis row (list view or project view) or the copy dialog needs to know about what a
 * quote can do RIGHT NOW, computed from data already in hand (the summary + its siblings) — no per-row
 * load. This is the single source of truth for menu-item enablement; the views must not re-derive it.
 */
export interface QuoteRowCapabilities {
  canCancel: boolean;
  canAccept: boolean;
  /**
   * Whether the quote may move straight to Approved. This is the OTHER half of Accept, not a separate
   * feature: with supplier review off (or a supplier that has already approved), Accepted is skipped
   * entirely and the same user gesture goes Issued -> Approved. Both capabilities are reported so the
   * row can pick the one that applies; see `buildQuoteRowActions`, which shows exactly one of them.
   */
  canApprove: boolean;
  canReject: boolean;
  /** Copy legality per mode. `outOfProject` is always allowed; the others depend on project + siblings. */
  copy: {
    alternative: boolean;
    newProjectQuote: boolean;
    outOfProject: boolean;
  };
  /**
   * Whether Delete may be offered. False while a copy/delete operation holds the quote — the server
   * refuses those deletes outright. NOT gated on `Locked`: a delete takes an operation-purpose lock,
   * which is a different key from the interactive one, so someone having the quote open never causes
   * a refusal. A foreign lock is still reported at click time by `QuoteLock`.
   */
  canDelete: boolean;
  /**
   * The quote's current lock state (Strand B). `Copying`/`Deleting` (an operation in progress) makes
   * the row read-only — every action disabled. `Locked` (someone has it open) blocks copy ONLY when
   * the quote's state still allows its shape to change; a settled quote stays copyable while held.
   */
  lockState: QuoteLockState;
}

export interface QuoteRowCapabilityOptions {
  /**
   * The already-resolved supplier-review gate: `false` for fabricator mode OR when the supplier's
   * config disables supplier review. Callers resolve it via resolveAllowSupplierReview().
   *
   * It is handed to the ENGINE and acts nowhere else. It was previously also AND-ed onto `canAccept`
   * here, which read as "no supplier review means you may not accept" — the opposite of the truth. With
   * review off the quote still advances on the same gesture; it just goes Issued -> Approved, skipping
   * Accepted, which is precisely the branch the engine already applies this flag to. AND-ing it here as
   * well disabled the card that was legal and offered nothing in its place, leaving an Issued quote with
   * no way forward from the row at all.
   */
  allowSupplierReview: boolean;
  /** Project state gate for copy legality; the list view (cross-project) may omit it (defaults true). */
  projectActive?: boolean;
  /**
   * Strand B: the quote's current lock state. `Copying`/`Deleting` → read-only (all actions disabled).
   * `Locked` (open elsewhere) → copy disabled only when the state still allows shape changes; other
   * actions unaffected. Defaults to None.
   */
  lockState?: QuoteLockState;
}

/**
 * THE supplier-review rule, as a pure function so it can be tested without an environment.
 *
 * Fabricator mode disables supplier review outright, whatever the supplier's own config says — that
 * precedence is the rule that reopened as PR 12764 thread 85021, so it is asserted by a test here
 * rather than left implicit at the call site. An uncached supplier defaults to ALLOWED, matching the
 * pre-existing behaviour: the config load is best-effort and a failed load must not silently strip
 * Accept from every row.
 *
 * `QuoteStateActions.resolveAllowSupplierReview()` is the caller — it supplies the two lookups
 * (fabricator flag, cached supplier config) and holds no rule of its own.
 */
export function resolveAllowSupplierReviewRule(
  isFabricatorMode: boolean,
  suppliersAllowSupplierReview: boolean | undefined
): boolean {
  if (isFabricatorMode) {
    return false;
  }
  return suppliersAllowSupplierReview ?? true;
}

/**
 * Group a flat sibling list (as returned by the summary endpoints) into a lookup by quote-set id.
 * One shared builder so the list view and the project resource view index siblings identically.
 */
export function buildSiblingMap(siblings: readonly QuoteSetSibling[]): Map<string, QuoteSetSibling[]> {
  const map = new Map<string, QuoteSetSibling[]>();
  for (const sibling of siblings) {
    const list = map.get(sibling.quoteSetId) ?? [];
    list.push(sibling);
    map.set(sibling.quoteSetId, list);
  }
  return map;
}

/**
 * Build the engine context from a summary row + the siblings loaded for its set. `isSupplierQuote` is
 * approximated from `supplierId` because the summary does not carry the true supplier-product flag; that
 * flag only gates the Approved transition (out of scope for cancel/accept/reject), so the approximation
 * cannot affect the three actions this model drives.
 *
 * THE ROW'S OWN QUOTE IS REMOVED FROM ITS SIBLING LIST HERE, and that is this function's real job.
 * Every rule in `QuoteSetSiblingManager` asks "does some OTHER quote in this set hold this state" — the
 * quote's own state is read separately, from `quoteState`. The full quote screen gets that for free: its
 * loader passes an excludeQuoteId (`QuoteDbContext.GetSiblingsOfQuoteAsync`) and self never arrives. The
 * summary endpoint cannot, because one batched query serves every row sharing a set and has no single
 * "self" to exclude (`TaskGetQuoteSummary.GetSiblingsForSetsAsync` documents that it returns the WHOLE
 * set deliberately). So the exclusion is per-row, and this is the one place every row passes through.
 *
 * Left in, a quote whose own state is one the sibling rules treat as blocking silently vetoes itself.
 * AB#230811 was the visible case: a reviewed quote that has been re-issued sits in Issued +
 * supplierApproved, which is exactly what `isSupplierIssued` looks for, so it made `workflowBusy` true
 * against itself and lost BOTH routes forward — the dialog offered a greyed Accept and a greyed Reject
 * on a quote the full quote screen was still offering Accept and Reject for.
 *
 * THE EXCLUSION IS ONLY CORRECT FOR THE "SOME OTHER QUOTE" RULES. `isNewQuoteAllowed` asks a set-level
 * question and must still see this row, so `computeQuoteRowCapabilities` puts self back for that one
 * rule. Taking self out of it wholesale is what let Copy as alternative appear on an Approved quote
 * (AB#230834); an earlier version of this comment recorded that as a side benefit, which was wrong.
 */
export function buildQuoteStateContext(summary: ViewQuoteSummary, siblings: QuoteSetSibling[]): IQuoteStateContext {
  return {
    quoteState: summary.state,
    quote: { supplierApproved: summary.supplierApproved },
    isSupplierQuote: summary.supplierId !== emptyGuid,
    siblings: siblings.filter(sibling => sibling.quoteId !== summary.id)
  };
}

/**
 * Compute the row capabilities. Consumed identically by the quote list ellipsis, the project resource
 * ellipsis, and the copy-choice dialog, so they can never disagree.
 */
export function computeQuoteRowCapabilities(
  ctx: IQuoteStateContext,
  options: QuoteRowCapabilityOptions
): QuoteRowCapabilities {
  const lockState = options.lockState ?? QuoteLockState.None;

  // An operation lock (a copy or delete in progress) overrides everything: the quote is read-only
  // until the operation finishes — the same guarantee the server enforces on update/delete.
  const operationInProgress = lockState === QuoteLockState.Copying || lockState === QuoteLockState.Deleting;
  if (operationInProgress) {
    return {
      canCancel: false,
      canAccept: false,
      canApprove: false,
      canReject: false,
      copy: { alternative: false, newProjectQuote: false, outOfProject: false },
      canDelete: false,
      lockState
    };
  }

  const engine = new UserQuoteStateEngine(ctx, options.allowSupplierReview);
  const projectActive = options.projectActive ?? true;

  // MIRRORS THE SERVER RULE EXACTLY (QuoteCopySafety): a copy is refused when the source's SHAPE can
  // change and something is in a position to change it. Being held is only half the test — the state
  // decides whether the holder could change anything a copy captures.
  //
  // So a settled quote stays copyable however long it is held: the holder has taken control of the
  // WORKFLOW (Accept/Reject), which changes nothing a copy takes, and a review change is made BY
  // copying rather than by editing in place. Greying Copy there would block the core workflow behind
  // someone else's open tab.
  //
  // Any divergence from the server here shows up as a row the user can click and the server then
  // refuses — which is exactly what this capability model exists to prevent.
  const shapeCouldChange = flagInSet(
    ctx.quoteState,
    // eslint-disable-next-line no-bitwise
    QuoteState.Draft | QuoteState.Active | QuoteState.SupplierReviewPending
  );

  // The SYSTEM is rewriting the quote — issuing generates documents and PO content, reset unwinds it,
  // and a copy is already in flight. The server refuses these outright, whatever any lock says, so the
  // cards must be disabled here too. NO LOCK IS INVOLVED, which is exactly why this needs its own
  // test: background work holds no interactive lock, so a lock-based rule sees the quote as free and
  // leaves Copy enabled on a quote the server will then refuse.
  const systemIsChangingIt = flagInSet(
    ctx.quoteState,
    // eslint-disable-next-line no-bitwise
    QuoteState.IssuePending | QuoteState.ResetInProgress | QuoteState.Copying
  );

  const locked = systemIsChangingIt || (lockState !== QuoteLockState.None && shapeCouldChange);

  // Copy legality mirrors the existing QuoteSetSiblingManager rules used by the full quote screen:
  //  - alternative:      project active AND the set can still take a new quote;
  //  - newProjectQuote:  project active;
  //  - outOfProject:     always (a standalone copy is never blocked by set/project state).
  // THE RULE: anything may be copied as an alternative, as long as THE SET is still in a state where
  // adding an alternative makes sense. Once the set's workflow has gone past the point where another
  // alternative is logical, it is not allowed. The row's own state is never a veto in its own right -
  // it counts only because the row is a member of the set it would be adding to.
  //
  // So `isNewQuoteAllowed` is the one SET-level rule in this manager and, unlike every other rule
  // here, it must see the WHOLE set including this row. The others ask "does some OTHER quote in the
  // set hold this state", which is why buildQuoteStateContext strips self from ctx.siblings
  // (AB#230811).
  //
  // Self is therefore put back, here and only here. Without it an Approved quote whose set happened to
  // hold no OTHER Approved quote reported an idle set and offered Copy as alternative on a won quote,
  // producing an approved quote carrying a purchase order beside a live alternative (AB#230834). Note
  // that passing the manager's optional `quote` is NOT enough: `isSupplierIssued` filters the sibling
  // array directly rather than going through `hasState`, so self has to be in the array to be seen.
  //
  // Only `state` and `supplierApproved` are read by any rule; the remaining fields exist to satisfy the
  // shape and are never consulted.
  const selfInSet: QuoteSetSibling = {
    quoteId: SELF_IN_SET_ID,
    quoteSetId: '',
    state: ctx.quoteState,
    alternativeType: QuoteAlternativeType.Standard,
    quoteNumber: 0,
    quoteSuffix: '',
    supplierApproved: ctx.quote?.supplierApproved ?? false,
    recordVersion: ''
  };
  const wholeSetManager = new QuoteSetSiblingManager(() => [...(ctx.siblings ?? []), selfInSet]);
  const alternativeAllowed = !locked && projectActive && wholeSetManager.isNewQuoteAllowed;

  return {
    canCancel: engine.isStateChangeAllowed(QuoteState.Cancelled),
    // Both halves of the accept gesture, each asked of the engine and nothing else. The engine already
    // holds allowSupplierReview (constructor) and applies it to exactly the branch it governs, so
    // neither of these needs — or may have — a second copy of that rule bolted on here.
    canAccept: engine.isStateChangeAllowed(QuoteState.Accepted),
    canApprove: engine.isStateChangeAllowed(QuoteState.Approved),
    canReject: engine.isStateChangeAllowed(QuoteState.Rejected),
    copy: {
      alternative: alternativeAllowed,
      newProjectQuote: !locked && projectActive,
      outOfProject: !locked
    },
    canDelete: true,
    lockState
  };
}
